import React, { useMemo } from 'react';
import { Segmented } from 'antd';
import { BankOutlined, UserOutlined } from '@ant-design/icons';
import { useIntl } from 'umi';
import { CUSTOMER_TYPES, CustomerType } from '@/typings/models/Contact';
import { useIsMobile } from '@/hooks/useResponsive';
import styles from './index.module.less';

type CustomerTypeToggleProps = {
  value: CustomerType;
  onChange: (type: CustomerType) => void;
  disabled?: boolean;
  size?: 'small' | 'middle';
};

/** Person | Company switch for a customer. */
const CustomerTypeToggle: React.FC<CustomerTypeToggleProps> = ({ value, onChange, disabled, size = 'small' }) => {
  const intl = useIntl();
  // Phones: icons only, so the switch fits beside the card title.
  const isMobile = useIsMobile();
  const options = useMemo(
    () =>
      [
        {
          value: CUSTOMER_TYPES.PERSON,
          icon: <UserOutlined />,
          text: intl.formatMessage({ id: 'component.contacts.typePerson', defaultMessage: 'Person' }),
        },
        {
          value: CUSTOMER_TYPES.ORGANIZATION,
          icon: <BankOutlined />,
          text: intl.formatMessage({ id: 'component.contacts.company', defaultMessage: 'Company' }),
        },
      ].map(({ text, ...option }) => ({ ...option, title: text, label: isMobile ? undefined : text })),
    [intl, isMobile],
  );

  return (
    <Segmented<CustomerType>
      className={styles.typeToggle}
      size={size}
      value={value}
      options={options}
      disabled={disabled}
      onChange={onChange}
      aria-label={intl.formatMessage({ id: 'common.attribute.type', defaultMessage: 'Type' })}
    />
  );
};

export default React.memo(CustomerTypeToggle);
